'use strict';
// ═══════════════════════════════════════════════════════════════
// src/OcApp.jsx — RiskMind OC (capital aberto) · feature auto-contida
//
// Um app-dentro-do-app: Screener + Ficha + Gráfico 10a com drill-down as-of.
// Consome /api/oc/* (routes/oc.js do backend) com o MESMO token/tenant do app.
//
// LOGIN ÚNICO / ENTITLEMENTS ADITIVOS: é a mesma conta do RiskMind privado.
// O portão é no backend (requireOc): se o usuário não tem OC (só empresas
// fechadas), a chamada volta 403 e aqui mostramos o upsell — sem quebrar. Se
// tem só OC, vê esta seção; o privado aparece pra quem tem `plan`. Um login, tudo.
//
// WIRE no App.jsx (mínimo — ver APPLY_frontend_oc.md):
//   import OcApp from './OcApp';
//   <button onClick={()=>setScreen('oc')}>RiskMind OC</button>
//   {screen==='oc' && <OcApp token={token} tenantId={TENANT?.id} />}
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import OcPainel from './OcPainel';
import OcCarteiraPanel from './OcCarteiraPanel';
import OcSelecao from './OcSelecao';
import OcMovimentacaoRisco from './OcMovimentacaoRisco';
import OcTriagem from './OcTriagem';
import OcRetrospectiva from './OcRetrospectiva';

const API_DEFAULT = 'https://api.financialmind.ai';
const C = {
  navy: '#0A1628', blue: '#1A3F6F', amber: '#C87B2A', gold: '#E8C56D', cream: '#FAF7F2',
  red: '#DC2626', green: '#15803D', amber2: '#D97706', agency: '#8067B7',
  muted: '#6b7280', line: '#e5e7eb', ink: '#0f172a', paper: '#ffffff',
};

// ── fetch layer: base + Bearer + X-Tenant-ID (padrão do app) ────
function useOcApi({ token, apiBase = API_DEFAULT, tenantId }) {
  return useCallback(async (path) => {
    const headers = { 'Content-Type': 'application/json' };
    if (tenantId) headers['X-Tenant-ID'] = tenantId;
    let tk = token;
    if (!tk && typeof localStorage !== 'undefined') {
      tk = localStorage.getItem('token') || localStorage.getItem('rm_token') || localStorage.getItem('jwt');
      if (!tk) { // fallback: varre o localStorage por um JWT (não sabemos a chave exata do app)
        for (let i = 0; i < localStorage.length; i++) {
          const v = localStorage.getItem(localStorage.key(i));
          if (v && /^eyJ[\w-]+\.[\w-]+\.[\w-]+$/.test(v)) { tk = v; break; }
        }
      }
    }
    if (tk) headers['Authorization'] = `Bearer ${tk}`;
    const r = await fetch(`${apiBase}${path}`, { headers });
    let data = null; try { data = await r.json(); } catch { /* corpo não-JSON */ }
    if (!r.ok) { const e = new Error((data && data.error) || `HTTP ${r.status}`); e.status = r.status; e.body = data; throw e; }
    return data;
  }, [token, apiBase, tenantId]);
}

// ── helpers ─────────────────────────────────────────────────────
const qlabel = (d) => { if (!d) return ''; const m = +d.slice(5, 7), q = m <= 3 ? 1 : m <= 6 ? 2 : m <= 9 ? 3 : 4; return q + 'T' + d.slice(2, 4); };
// Três unidades chegam nesta tela e só uma delas é probabilidade em [0,1].
// O nome de cada formatador diz o que ele aceita; misturá-los foi o defeito
// que imprimia 0.1% para uma PD de 10,3% e 4200% para o percentil 42.
// fmtPd: valor JÁ em pontos percentuais — /api/oc/historico (pd_raw, pd_cal),
// e o eixo do gráfico, que buildGeo declara em p.p. via Math.max(10, ...).
const fmtPd = (v) => v == null ? '—' : (+v).toFixed(1) + '%';
// fmtProb01: probabilidade em [0,1] — /api/oc/universo e /api/oc/empresa.
const fmtProb01 = (v) => v == null ? '—' : (100 * +v).toFixed(1) + '%';
// fmtPctil: percentil em [0,100] — sector_pct, que o banco já grava nessa escala.
const fmtPctil = (v) => v == null ? '—' : Math.round(+v) + '%';
const fmtN = (v, d = 2) => v == null ? '—' : (+v).toFixed(d);
const ratingColor = (r) => ['CC', 'C', 'D'].includes(r) ? C.red : r === 'CCC' ? C.amber : ['A', 'AA', 'AAA'].includes(r) ? C.green : C.blue;
// sector_pct é percentil em [0,100]: acima de 75 é cauda ruim do setor, abaixo de 50 é metade boa.
const pctColor = (p) => p == null ? C.muted : p >= 75 ? C.red : p >= 50 ? C.amber2 : C.green;

// ═══════════════════════════════════════════════════════════════
// Root
// ═══════════════════════════════════════════════════════════════
// — navegação entre as duas telas do OC ——————————————————
// A carteira monitorada é o produto contratado; o screener é o universo
// inteiro. Ao promover a carteira a tela inicial, o screener PRECISA continuar
// alcançável — senão a emenda esconderia uma tela que já funciona.
/* A aba "Todas as companhias" fica travada ate a carteira ser confirmada.

   Razao comercial: o universo inteiro e o que se vende por pacote. Abrir a
   lista antes de o cliente gastar um slot entrega a vitrine e a loja juntas.

   Quem nao tem pacote — o uso interno — segue com a aba livre: ali nao ha
   carteira a montar e o universo sempre foi o produto. */
/* A terceira aba, "Movimentacao de risco", segue o MESMO portao do universo.
   Ela responde "quem se deteriorou e nao tem registro publico" sobre o universo
   inteiro, nao sobre a carteira — entao liberar ela antes da carteira
   confirmada entregaria a vitrine e a loja juntas, exatamente o que a trava da
   aba "Todas as companhias" existe para impedir. Mesmo criterio, mesma trava. */
function OcNav({ atual, onCarteira, onScreener, onMovimentacao, onTriagem, onRetrospectiva, screenerLiberado = true }) {
  const bt = (ativo, travado) => ({
    padding: '7px 14px',
    borderRadius: 999,
    cursor: travado ? 'not-allowed' : 'pointer',
    fontSize: 13,
    fontWeight: ativo ? 700 : 500,
    border: '1px solid ' + (ativo ? C.navy : C.line),
    background: ativo ? C.navy : C.paper,
    color: ativo ? C.paper : C.muted,
    opacity: travado ? 0.5 : 1,
  });
  return (
    <div style={{ display: 'flex', gap: 8, margin: '0 0 16px' }}>
      <button style={bt(atual === 'carteira', false)} onClick={onCarteira}>
        Carteira monitorada
      </button>
      <button
        style={bt(atual === 'screener', !screenerLiberado)}
        onClick={screenerLiberado ? onScreener : undefined}
        disabled={!screenerLiberado}
        title={screenerLiberado ? undefined : 'Disponivel depois que a carteira for confirmada.'}
      >
        Todas as companhias
      </button>
      <button
        style={bt(atual === 'movimentacao', !screenerLiberado)}
        onClick={screenerLiberado ? onMovimentacao : undefined}
        disabled={!screenerLiberado}
        title={screenerLiberado ? undefined : 'Disponivel depois que a carteira for confirmada.'}
      >
        Movimentação de risco
      </button>
      <button
        style={bt(atual === 'triagem', !screenerLiberado)}
        onClick={screenerLiberado ? onTriagem : undefined}
        disabled={!screenerLiberado}
        title={screenerLiberado ? undefined : 'Disponivel depois que a carteira for confirmada.'}
      >
        Triagem
      </button>
      {/* A retrospectiva fala de companhias que JA TEM ato publico de insolvencia
          registrado na CVM — informacao publica por definicao. Nao ha vitrine a
          proteger aqui, e por isso ela NAO entra na trava de `screenerLiberado`:
          e justamente o material que o cliente novo precisa ver para decidir se
          monta a carteira. O portao comercial continua sendo `requireOc`, no
          servidor. */}
      <button style={bt(atual === 'retrospectiva', false)} onClick={onRetrospectiva}>
        Retrospectiva
      </button>
    </div>
  );
}

export default function OcApp({ token, tenantId, apiBase = API_DEFAULT }) {
  const api = useOcApi({ token, apiBase, tenantId });
  // A carteira monitorada passa a ser a tela inicial: é o que o assinante
  // contratou e é onde está o ciclo vigente. O screener fica a um clique.
  const [screen, setScreen] = useState('carteira'); // 'carteira' | 'screener' | 'movimentacao' | 'triagem' | 'retrospectiva' | 'ficha'

  /* Estado do contrato de OC, so para saber se a aba do universo abre.
     Falha ABERTA de proposito: se a chamada nao responder, a aba fica liberada
     como sempre esteve. Travar o produto por causa de um erro de rede seria
     pior do que o contrario. */
  const [oc, setOc] = useState(null);
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        let tk = token;
        if (!tk && typeof localStorage !== 'undefined') {
          tk = localStorage.getItem('riskmind_token') || localStorage.getItem('token') || null;
        }
        const r = await fetch(apiBase + '/api/oc/carteira', {
          headers: tk ? { Authorization: 'Bearer ' + tk } : {},
        });
        const j = r.ok ? await r.json() : { status: 'indisponivel' };
        if (vivo) setOc(j);
      } catch (e) {
        if (vivo) setOc({ status: 'indisponivel' });
      }
    })();
    return () => { vivo = false; };
  }, [apiBase, token]);

  const screenerLiberado =
    !oc ||
    oc.status === 'indisponivel' ||
    oc.status === 'sem_ciclo' ||
    !oc.plan ||
    !!oc.selecao_confirmada_em;

  /* QUEM TEM CARTEIRA VE A CARTEIRA, NAO O UNIVERSO.
     Depois de confirmada, "Todas as companhias" passou a listar as 553 abertas
     com todas as linhas clicaveis. O portao do servidor segurava (routes/oc.js,
     exigeNaCarteira: empresa fora da carteira devolve 403), mas o cliente via
     uma vitrine inteira e so descobria no clique que 549 delas nao eram dele —
     era prometer o que o backend ia negar. A lista agora e a propria carteira.
     O universo completo continua sendo o que se vende: quem quiser mais nomes
     preenche vaga livre ou troca, na tela de gestao.

     Uso interno (@financialmind.ai, sem plano/sem ciclo) nao entra aqui: la
     `plan` e nulo e a lista segue completa, como sempre foi. */
  const cnpjsDaCarteira = useMemo(() => {
    if (!oc || !oc.plan || !oc.selecao_confirmada_em) return null;
    const s = new Set();
    for (const e of (oc.empresas || [])) s.add(String(e.cnpj || '').replace(/\D/g, ''));
    return s;
  }, [oc]);
  const [cnpj, setCnpj] = useState(null);
  // De onde o usuário abriu a ficha. Sem isto, "voltar" devolveria todo mundo
  // à carteira e quem veio do screener perderia a ordenação que montou.
  const [origem, setOrigem] = useState('carteira');

  const openFicha = (c) => {
    // ficha -> ficha (clique de dentro da ficha) preserva a origem original.
    setOrigem(screen === 'ficha' ? origem : screen);
    setCnpj(c); setScreen('ficha'); window.scrollTo(0, 0);
  };
  const back = () => { setScreen(origem); setCnpj(null); window.scrollTo(0, 0); };
  const verCarteira = () => { setScreen('carteira'); setCnpj(null); window.scrollTo(0, 0); };
  const verScreener = () => { setScreen('screener'); setCnpj(null); window.scrollTo(0, 0); };
  const verMovimentacao = () => { setScreen('movimentacao'); setCnpj(null); window.scrollTo(0, 0); };
  const verTriagem = () => { setScreen('triagem'); setCnpj(null); window.scrollTo(0, 0); };
  const verRetrospectiva = () => { setScreen('retrospectiva'); setCnpj(null); window.scrollTo(0, 0); };

  return (
    <div style={{ background: C.cream, minHeight: '100%', color: C.ink, fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
      <div style={{ maxWidth: 1160, margin: '0 auto', padding: '18px 20px 60px' }}>
        <Header />
        {screen !== 'ficha' && (
          <OcNav atual={screen} onCarteira={verCarteira} onScreener={verScreener} onMovimentacao={verMovimentacao} onTriagem={verTriagem} onRetrospectiva={verRetrospectiva} screenerLiberado={screenerLiberado} />
        )}
        {/* `movimentacao` entra no MESMO ramo de queda de `screener`: sem
            carteira confirmada, cai na tela de selecao em vez de abrir o
            universo. Sem isto, a aba nova seria o buraco no portao. */}
        {screen === 'carteira' || ((screen === 'screener' || screen === 'movimentacao' || screen === 'triagem') && !screenerLiberado)
          ? <>
            {/* Montar a carteira. O motor e a validacao sao do servidor
                (services/oc/oc_watchlist.js); esta tela so da a mao para o
                admin do cliente escolher as empresas do plano dele. Fica
                acima do painel porque, enquanto a selecao esta pendente, e
                a unica coisa que importa nesta aba.

                O fragmento nao e enfeite: este ramo do ternario aceita UM
                elemento so. Sem ele, dois irmaos aqui viram
                'Unexpected token, expected ":"' no Babel — foi o que
                derrubou o build 373. */}
            <OcSelecao token={token} apiBase={apiBase} onPronto={setOc} />
            <OcCarteiraPanel api={api} onOpen={openFicha} />
          </>
          : screen === 'screener'
            ? <Screener api={api} onOpen={openFicha} apenas={cnpjsDaCarteira} />
            : screen === 'movimentacao'
              ? <OcMovimentacaoRisco api={api} onOpen={openFicha} />
              : screen === 'triagem'
                ? <OcTriagem api={api} onOpen={openFicha} />
                : screen === 'retrospectiva'
                  ? <OcRetrospectiva api={api} />
                  : <Ficha api={api} cnpj={cnpj} onBack={back} />}
      </div>
    </div>
  );
}

function Header() {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, borderBottom: `2px solid ${C.navy}`, paddingBottom: 10, marginBottom: 16 }}>
      <span style={{ fontSize: 20, fontWeight: 800, color: C.navy, letterSpacing: -0.3 }}>RiskMind <span style={{ color: C.amber }}>OC</span></span>
      <span style={{ fontSize: 12.5, color: C.muted }}>Capital aberto · PD calibrada, trajetória de 10 anos e previsão 12m</span><a href='/' style={{ marginLeft: 'auto', fontSize: 12.5, color: C.blue, textDecoration: 'none', fontWeight: 600 }}>Empresas fechadas -></a>
    </div>
  );
}

// ── mensagem de portão (403) → beta interno ou upsell ───────────
/* Tres 403 diferentes chegam aqui e so um deles e falta de produto.
   Ate a v3.38.0 havia um so, e o texto de upsell servia. Depois que routes/oc.js
   passou a barrar ficha de empresa fora da carteira, o mesmo cartao passou a
   dizer "voce ainda nao tem o RiskMind OC" para quem TEM o produto e so pediu
   uma companhia que nao esta na carteira dele — resposta errada, e das que
   fazem o cliente ligar para o comercial achando que perdeu o acesso.
   O codigo vem em `erro` (portao novo) ou em `error` (portoes antigos). */
function GateNotice({ err }) {
  const cod = (err && err.body && (err.body.erro || err.body.error)) || null;
  const textos = {
    oc_beta_interno: [
      'RiskMind OC — beta interno',
      'O OC está em validação interna. O acesso está liberado apenas para contas @financialmind.ai neste momento.',
    ],
    empresa_fora_da_carteira: [
      'Companhia fora da sua carteira',
      'A ficha completa é do que você monitora. Esta companhia não está na carteira deste ciclo — inclua-a numa vaga livre do plano (não gasta troca) na aba Carteira monitorada e a ficha abre em seguida.',
    ],
    carteira_nao_confirmada: [
      'Carteira ainda não montada',
      'As fichas abrem depois que a carteira do ciclo for confirmada. Escolha as companhias na aba Carteira monitorada.',
    ],
  };
  const [titulo, corpo] = textos[cod] || [
    'RiskMind OC',
    'Você ainda não tem o RiskMind OC no seu plano. Fale com o comercial para incluir o add-on de capital aberto na sua conta atual — mesmo login, sem migração.',
  ];
  return (
    <div style={{ background: C.paper, border: `1px solid ${C.line}`, borderRadius: 12, padding: '26px 24px', textAlign: 'center', marginTop: 24 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.navy, marginBottom: 6 }}>{titulo}</div>
      <div style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.5, maxWidth: 520, margin: '0 auto' }}>{corpo}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Screener
// ═══════════════════════════════════════════════════════════════
const COLS = [
  { k: 'denom', label: 'empresa', al: 'left' },
  { k: 'setor', label: 'setor', al: 'left' },
  { k: 'rating', label: 'rating', al: 'center' },
  { k: 'pd', label: 'prob. inadimpl.', al: 'right' },
  { k: 'sector_pct', label: 'posição no setor', al: 'right' },
];

function Screener({ api, onOpen, apenas }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState('');
  const [setor, setSetor] = useState('');
  const [setores, setSetores] = useState([]);
  const [sort, setSort] = useState('pd');
  const [dir, setDir] = useState('desc');

  useEffect(() => {
    let live = true;
    setErr(null); setRows(null);
    const qs = new URLSearchParams();
    if (setor) qs.set('setor', setor);
    if (q) qs.set('q', q);
    qs.set('sort', sort); qs.set('dir', dir); qs.set('limit', '500');
    api(`/api/oc/universo?${qs.toString()}`)
      .then((d) => { if (live) setRows(d.rows || []); })
      .catch((e) => { if (live) setErr(e); });
    return () => { live = false; };
  }, [api, setor, q, sort, dir]);

  useEffect(() => {
    let live = true;
    api('/api/oc/setores').then((d) => { if (live) setSetores(Array.isArray(d) ? d : []); }).catch(() => {});
    return () => { live = false; };
  }, [api]);

  if (err && (err.status === 403)) return <GateNotice err={err} />;

  const onSort = (k) => { if (sort === k) setDir(dir === 'asc' ? 'desc' : 'asc'); else { setSort(k); setDir(k === 'denom' || k === 'setor' ? 'asc' : 'desc'); } };

  // O filtro e por CNPJ so-digitos porque /api/oc/universo devolve mascarado
  // (05.303.439/0001-07) e /api/oc/carteira devolve cru (05303439000107).
  // Comparar as duas formas direto nao casa nunca.
  const linhas = rows && apenas ? rows.filter((r) => apenas.has(String(r.cnpj || '').replace(/\D/g, ''))) : rows;

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="buscar empresa ou ticker…"
          style={inp} />
        <select value={setor} onChange={(e) => setSetor(e.target.value)} style={inp}>
          <option value="">todos os setores</option>
          {setores.map((s) => <option key={s.setor} value={s.setor}>{s.setor} ({s.n})</option>)}
        </select>
        {linhas && <span style={{ alignSelf: 'center', fontSize: 12, color: C.muted }}>{linhas.length} empresas</span>}
      </div>

      {apenas && (
        <div style={{ background: C.paper, border: `1px solid ${C.line}`, borderLeft: `3px solid ${C.amber}`, borderRadius: 10, padding: '10px 14px', fontSize: 12.5, color: C.ink, lineHeight: 1.5, marginBottom: 12 }}>
          Esta lista mostra as <b>{apenas.size}</b> companhias da sua carteira. O universo do
          RiskMind tem 553 abertas — para acompanhar outras, inclua nas vagas livres do plano
          (não gasta troca) na aba <b>Carteira monitorada</b>.
        </div>
      )}

      {err && err.status !== 403 && <div style={errBox}>Erro ao carregar: {err.message}</div>}
      {!rows && !err && <div style={{ color: C.muted, fontSize: 13, padding: 20 }}>carregando universo…</div>}

      {linhas && (
        <div style={{ background: C.paper, border: `1px solid ${C.line}`, borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {COLS.map((c) => (
                  <th key={c.k} onClick={() => onSort(c.k)}
                    style={{ textAlign: c.al, padding: '9px 12px', cursor: 'pointer', color: C.muted, fontWeight: 600, fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${C.line}`, whiteSpace: 'nowrap' }}>
                    {c.label}{sort === c.k ? (dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {linhas.map((r) => (
                <tr key={r.cnpj} onClick={() => onOpen(r.cnpj)}
                  style={{ cursor: 'pointer', borderBottom: `1px solid #f1f5f9` }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = ''}>
                  <td style={{ padding: '9px 12px' }}>
                    <span style={{ fontWeight: 600 }}>{r.denom || '—'}</span>
                    {r.ticker && <span style={{ color: C.muted, marginLeft: 6, fontSize: 11.5 }}>{r.ticker}</span>}
                  </td>
                  <td style={{ padding: '9px 12px', color: C.muted }}>{r.setor || '—'}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                    <span style={{ ...badge, color: ratingColor(r.rating), borderColor: ratingColor(r.rating) }}>{r.rating || '—'}</span>
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtProb01(r.pd)}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: pctColor(r.sector_pct) }}>
                    {fmtPctil(r.sector_pct)}
                  </td>
                </tr>
              ))}
              {linhas.length === 0 && <tr><td colSpan={5} style={{ padding: 20, color: C.muted, textAlign: 'center' }}>nada no filtro atual.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Ficha (empresa) + Gráfico
// ═══════════════════════════════════════════════════════════════
function Ficha({ api, cnpj, onBack }) {
  const [ficha, setFicha] = useState(null);
  const [hist, setHist] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let live = true; setErr(null); setFicha(null); setHist(null);
    Promise.all([
      api(`/api/oc/empresa/${encodeURIComponent(cnpj)}`).catch((e) => { throw e; }),
      api(`/api/oc/historico/${encodeURIComponent(cnpj)}`).catch(() => null),
    ]).then(([f, h]) => { if (live) { setFicha(f); setHist(h); } })
      .catch((e) => { if (live) setErr(e); });
    return () => { live = false; };
  }, [api, cnpj]);

  if (err && err.status === 403) return <GateNotice err={err} />;

  const emp = ficha && ficha.empresa;
  const at = ficha && ficha.atual;

  return (
    <div>
      <button onClick={onBack} style={{ ...btn, marginBottom: 14 }}>← universo</button>
      {err && err.status !== 403 && <div style={errBox}>Erro: {err.message}</div>}
      {!ficha && !err && <div style={{ color: C.muted, fontSize: 13, padding: 20 }}>carregando ficha…</div>}

      {emp && (
        <div style={{ background: C.paper, border: `1px solid ${C.line}`, borderRadius: 12, padding: '16px 18px', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 18, fontWeight: 800, color: C.navy }}>{emp.denom}</span>
            {emp.ticker && <span style={{ color: C.muted }}>{emp.ticker}</span>}
            <span style={{ color: C.muted, fontSize: 12.5 }}>· {emp.setor}</span>
            <span style={{ marginLeft: 'auto', fontSize: 11.5, color: C.muted }}>base {String(emp.data_base).slice(0, 10)}</span>
          </div>
          {at && (
            <div style={{ display: 'flex', gap: 26, marginTop: 12, flexWrap: 'wrap' }}>
              <Metric label="PD (motor)" value={fmtProb01(at.pd)} color={C.blue} />
              <Metric label="rating" value={at.rating || '—'} color={ratingColor(at.rating)} />
              <Metric label="posição no setor" value={fmtPctil(at.sector_pct)} color={pctColor(at.sector_pct)} />
              <Metric label="Merton" value={at.merton_source || '—'} color={C.ink} />
              <Metric label="mercado" value={at.market_present ? 'sim' : 'não'} color={C.ink} />
            </div>
          )}
        </div>
      )}

      {hist && hist.history && hist.history.length > 0
        ? <ChartCard api={api} cnpj={cnpj} H={hist} />
        : ficha && <div style={{ ...errBox, background: '#f8fafc', color: C.muted, border: `1px solid ${C.line}` }}>
            Trajetória indisponível para esta companhia. A posição apurada acima continua válida.
          </div>}
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Gráfico: PD calibrada vs raw (motor) + previsão + agência + drill-down
// ═══════════════════════════════════════════════════════════════
function ChartCard({ api, cnpj, H }) {
  const [showRaw, setShowRaw] = useState(true);
  const [showAg, setShowAg] = useState(true);
  const [hover, setHover] = useState(null);   // idx do ponto hist
  const [pick, setPick] = useState(null);     // {loading|data|null, date}
  const wrapRef = useRef(null);

  const geo = useMemo(() => buildGeo(H), [H]);

  const onPick = useCallback((pt) => {
    if (pt.forecast) { setPick({ forecast: true, date: pt.date }); return; }
    setPick({ loading: true, date: pt.date });
    api(`/api/oc/historico/${encodeURIComponent(cnpj)}/${encodeURIComponent(pt.date)}`)
      .then((d) => setPick({ data: d, date: pt.date }))
      .catch(() => setPick({ data: null, date: pt.date }));
  }, [api, cnpj]);

  return (
    <div style={{ background: C.paper, border: `1px solid ${C.line}`, borderRadius: 12, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, color: C.navy }}>{H && H.forecast && H.forecast.indisponivel ? 'Trajetória de PD · série do banco' : 'Trajetória de PD · 10 anos + previsão'}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 14, fontSize: 12 }}>
          <Legend color={C.blue} label="PD calibrada" />
          {geo.raws && geo.raws.some((v) => v != null) ? <Toggle on={showRaw} set={setShowRaw} color={C.amber} label="PD motor (raw)" dash /> : null}
          {geo.ags && geo.ags.some((v) => v != null) ? <Toggle on={showAg} set={setShowAg} color={C.agency} label="agências" /> : null}
        </div>
      </div>

      <div ref={wrapRef} style={{ position: 'relative' }}>
        <ChartSVG geo={geo} showRaw={showRaw} showAg={showAg} hover={hover} setHover={setHover} onPick={onPick} />
        {hover != null && geo.hist[hover] && <Tooltip pt={geo.hist[hover]} geo={geo} idx={hover} />}
      </div>

      <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4 }}>
        passe o mouse para ver o trimestre · <b>clique</b> para o detalhe as-of no banco
      </div>

      <DetailCard pick={pick} />
      <OcPainel api={api} cnpj={cnpj} />
    </div>
  );
}

function buildGeo(H) {
  /* A UNIDADE VEM CARIMBADA NO DADO, E E LIDA AQUI — UMA VEZ SO.
     Ate 26/08/2026 /historico devolvia pd_cal em PONTOS PERCENTUAIS (heranca
     de oc_history, tabela que nenhum job escrevia) enquanto /empresa devolvia
     atual.pd em FRACAO. Duas rotas, duas unidades, e a tela adivinhando qual
     era qual — medido na Braskem: 0.3378 numa, 32.5 na outra, mesmo dado.
     Agora a API carimba pd_unidade. Backend antigo nao manda carimbo, e a
     ausencia significa pontos percentuais: por isso a conversao e CONDICIONAL
     e nao um multiplicador fixo. Assim esta tela funciona com os dois, e o
     dia da troca nao vira um grafico 100x errado com cara de certo. */
  const emFracao = !!(H && H.pd_unidade === 'fracao');
  const pp = (v) => (v == null ? null : (emFracao ? v * 100 : v));
  const hist = (H.history || []).map((r) => ({
    ...r, pd_cal: pp(r.pd_cal), pd_raw: pp(r.pd_raw), agency_pd: pp(r.agency_pd),
  }));
  const fpath = (H.forecast && H.forecast.path) || [];
  const markers = H.markers || [];
  const allDates = hist.map((r) => r.date).concat(fpath.map((p) => p.date));
  const cals = hist.map((r) => r.pd_cal);
  const raws = hist.map((r) => r.pd_raw);
  const ags = hist.map((r) => r.agency_pd);
  const fc = fpath.map((p) => pp(p.pd));
  const band = (H.forecast && H.forecast.band) ? H.forecast.band.map(pp) : null;
  const vmax = Math.max(10, ...cals.filter((v) => v != null), ...raws.filter((v) => v != null),
    ...fc.filter((v) => v != null), ...(band || []));
  const W = 860, Ht = 260, pl = 40, pr = 14, pt = 14, pb = 26;
  const n = allDates.length;
  const X = (i) => pl + (n <= 1 ? 0 : (i / (n - 1)) * (W - pl - pr));
  const Y = (v) => pt + (1 - (v / (vmax * 1.08))) * (Ht - pt - pb);
  const iT = hist.length - 1; // último ponto histórico
  return { W, Ht, pl, pr, pt, pb, X, Y, vmax, hist, fpath, markers, cals, raws, ags, fc, band, iT, n, allDates };
}

function ChartSVG({ geo, showRaw, showAg, hover, setHover, onPick }) {
  const { W, Ht, pl, pr, pt, pb, X, Y, hist, fpath, markers, iT } = geo;
  const line = (arr, off = 0) => arr.map((v, i) => v == null ? null : `${X(i + off).toFixed(1)},${Y(v).toFixed(1)}`).filter(Boolean).join(' ');

  // agências em degraus (mantém o valor até o próximo ponto)
  let agPts = '';
  if (showAg) {
    const sp = [];
    for (let i = 0; i <= iT; i++) {
      const v = hist[i].agency_pd; if (v == null) continue;
      sp.push(`${X(i).toFixed(1)},${Y(v).toFixed(1)}`);
      if (i < iT) sp.push(`${X(i + 1).toFixed(1)},${Y(v).toFixed(1)}`);
    }
    agPts = sp.join(' ');
  }
  const calPts = line(geo.cals);
  const rawPts = line(geo.raws);
  const fcPts = fpath.length ? `${X(iT).toFixed(1)},${Y(geo.cals[iT]).toFixed(1)} ` + fpath.map((p, i) => `${X(iT + 1 + i).toFixed(1)},${Y(p.pd).toFixed(1)}`).join(' ') : '';

  // banda de previsão (retângulo simples no horizonte)
  let bandRect = null;
  if (geo.band && fpath.length) {
    const x0 = X(iT), x1 = X(iT + fpath.length);
    const yLo = Y(geo.band[0]), yHi = Y(geo.band[1]);
    bandRect = <rect x={x0} y={Math.min(yLo, yHi)} width={x1 - x0} height={Math.abs(yLo - yHi)} fill={C.blue} opacity="0.08" />;
  }

  const nearest = (evt) => {
    const svg = evt.currentTarget.ownerSVGElement || evt.currentTarget;
    const r = svg.getBoundingClientRect();
    const px = (evt.clientX - r.left) / r.width * W;
    let best = -1, bd = 1e9;
    for (let i = 0; i < hist.length; i++) { const d = Math.abs(X(i) - px); if (d < bd) { bd = d; best = i; } }
    return best;
  };

  const yticks = [0, 0.25, 0.5, 0.75, 1].map((f) => { const v = geo.vmax * 1.08 * (1 - f); return { y: pt + f * (Ht - pt - pb), v }; });

  return (
    <svg viewBox={`0 0 ${W} ${Ht}`} width="100%" style={{ maxWidth: W, display: 'block' }}>
      {hist.map((r, i) => (i % Math.max(1, Math.ceil(hist.length / 6)) === 0 || i === hist.length - 1) ? <text key={'xq' + i} x={X(i)} y={Ht - 6} fontSize="8.5" fill="#6b7280" textAnchor="middle">{qlabel(r.date)}</text> : null)}
      {yticks.map((t, i) => (
        <g key={i}>
          <line x1={pl} y1={t.y} x2={W - pr} y2={t.y} stroke={C.line} strokeWidth="1" />
          <text x={4} y={t.y + 3} fontSize="9" fill={C.muted}>{Math.round(t.v)}%</text>
        </g>
      ))}
      {bandRect}
      {markers.map((m, i) => {
        const idx = geo.allDates.indexOf(m.date); if (idx < 0) return null;
        return (
          <g key={i}>
            <line x1={X(idx)} y1={pt} x2={X(idx)} y2={Ht - pb} stroke={C.red} strokeWidth="1" strokeDasharray="2,3" opacity="0.5" />
            <text x={X(idx)} y={pt + 8} fontSize="8.5" fill={C.red} textAnchor="middle">{m.label}</text>
          </g>
        );
      })}
      {showAg && agPts && <polyline points={agPts} fill="none" stroke={C.agency} strokeWidth="1.8" strokeLinejoin="round" opacity="0.9" />}
      {showRaw && rawPts && <polyline points={rawPts} fill="none" stroke={C.amber} strokeWidth="1.5" strokeDasharray="4,3" opacity="0.85" />}
      {fcPts && <polyline points={fcPts} fill="none" stroke={C.blue} strokeWidth="2.2" strokeDasharray="5,4" />}
      {calPts && <polyline points={calPts} fill="none" stroke={C.blue} strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round" />}
      {hover != null && hist[hover] && hist[hover].pd_cal != null && (
        <circle cx={X(hover)} cy={Y(hist[hover].pd_cal)} r="4.5" fill={C.blue} stroke="#fff" strokeWidth="1.5" />
      )}
      <rect x={pl} y={pt} width={W - pl - pr} height={Ht - pt - pb} fill="transparent" style={{ cursor: 'pointer' }}
        onMouseMove={(e) => setHover(nearest(e))}
        onMouseLeave={() => setHover(null)}
        onClick={(e) => { const i = nearest(e); if (i >= 0 && hist[i]) onPick({ date: hist[i].date }); }} />
    </svg>
  );
}

function Tooltip({ pt, geo, idx }) {
  const left = Math.min(Math.max(geo.X(idx) / geo.W * 100, 8), 82);
  return (
    <div style={{ position: 'absolute', left: `${left}%`, top: 6, transform: 'translateX(-50%)', background: C.navy, color: '#fff', padding: '7px 10px', borderRadius: 8, fontSize: 11.5, pointerEvents: 'none', whiteSpace: 'nowrap', boxShadow: '0 4px 14px rgba(0,0,0,.18)' }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>{qlabel(pt.date)} · {pt.date}</div>
      <div>PD calibrada <b>{fmtPd(pt.pd_cal)}</b> · motor {fmtPd(pt.pd_raw)}</div>
      <div style={{ opacity: 0.85 }}>rating {pt.rating || '—'}{pt.agency_rating ? ` · agência ${pt.agency_rating}` : ''}</div>
      <div style={{ fontSize: 9.5, opacity: 0.7, marginTop: 2 }}>clique → detalhe do banco</div>
    </div>
  );
}

function DetailCard({ pick }) {
  if (!pick) return null;
  const box = { marginTop: 12, background: '#f8fafc', border: `1px solid ${C.line}`, borderRadius: 10, padding: '12px 14px' };
  if (pick.forecast) return <div style={box}><b>{qlabel(pick.date)} · {pick.date}</b> — ponto de previsão, sem detalhe as-of no banco.</div>;
  if (pick.loading) return <div style={box}>carregando detalhe as-of…</div>;
  if (!pick.data) return <div style={box}>Sem detalhe para este trimestre.</div>;
  const d = pick.data, dt = d.detail || {};
  const cell = (k, v) => (
    <div style={{ minWidth: 92 }}>
      <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.3 }}>{k}</div>
      <div style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{v}</div>
    </div>
  );
  return (
    <div style={box}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
        <b style={{ color: C.navy }}>{qlabel(d.data_base)} · {String(d.data_base).slice(0, 10)}</b>
        <span style={{ fontSize: 11.5, color: C.muted }}>detalhe as-of do banco (inputs do modelo na época)</span>
      </div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
        {cell('PD calibr.', fmtPd(d.pd_cal))}
        {cell('PD motor', fmtPd(d.pd_raw))}
        {cell('rating', d.rating || '—')}
        {cell('Kanitz', d.kanitz || '—')}
        {cell('agência', d.agency_rating || '—')}
        {cell('liquidez', fmtN(dt.liquidez))}
        {cell('alavanc.', fmtN(dt.alavancagem))}
        {cell('PL/ativo', fmtN(dt.pl_ativo))}
        {cell('margem EBIT', dt.margem_ebit == null ? '—' : (dt.margem_ebit * 100).toFixed(1) + '%')}
        {cell('cobertura', fmtN(dt.cobertura))}
        {cell('Altman Z', fmtN(dt.altman_z))}
        {cell('zona', dt.altman_zona || '—')}
      </div>
    </div>
  );
}

// ── mini UI ─────────────────────────────────────────────────────
function Legend({ color, label }) { return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: C.muted }}><span style={{ width: 14, height: 3, background: color, display: 'inline-block', borderRadius: 2 }} />{label}</span>; }
function Toggle({ on, set, color, label, dash }) {
  return (
    <button onClick={() => set(!on)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', color: on ? C.muted : '#c0c6cf', fontSize: 12, padding: 0 }}>
      <span style={{ width: 14, height: 3, background: on ? color : '#d1d5db', display: 'inline-block', borderRadius: 2, backgroundImage: dash && on ? `repeating-linear-gradient(90deg,${color} 0 4px,transparent 4px 7px)` : 'none' }} />{label}
    </button>
  );
}

const inp = { padding: '8px 11px', border: `1px solid ${C.line}`, borderRadius: 9, fontSize: 13, background: C.paper, color: C.ink, outline: 'none', minWidth: 150 };
const btn = { padding: '7px 13px', border: `1px solid ${C.line}`, borderRadius: 9, background: C.paper, color: C.navy, cursor: 'pointer', fontSize: 13, fontWeight: 600 };
const badge = { display: 'inline-block', minWidth: 34, padding: '2px 7px', border: '1.5px solid', borderRadius: 6, fontSize: 11.5, fontWeight: 700 };
const errBox = { background: '#fef2f2', border: '1px solid #fecaca', color: C.red, borderRadius: 10, padding: '12px 14px', fontSize: 13, marginTop: 12 };
